import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { ViewModel as ArriveData } from 'TypeGen/Order/Arrive/view-model';
import { Command as ArriveRequest } from 'TypeGen/Order/Arrive/command';
import { Loader } from '@progress/kendo-react-indicators';
import { JsonResponse } from 'TypeGen/json-response';
import { useEffect, useState } from 'react';
import { ApiLink } from 'TypeGen/api-link';
import { ApiCommand } from 'TypeGen/api-command';
import { fetchApi } from 'core/fetchApi';
import { parseJSON } from 'date-fns';
import { formatNumber } from '@progress/kendo-intl';
import { formatDateTime } from 'utils/format';
import { useTrack } from '../context';
import { Grid, Typography, Link, Box } from '@mui/material';

type Props = {
  Link: ApiLink;
  CloseDialog: () => void;
}

const Arrive = (props: Props) => {

  const track = useTrack();
  const [data, setData] = useState<ArriveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dateTime, setDateTime] = useState<Date | null>(new Date());
  const [reasonLate, setReasonLate] = useState<number>(0);

  const arrive = () => {
    if (!dateTime) {
      alert('Please select a date/time');
      return;
    }

    setSaving(true);
    const link = data!.Links.find(x => x.Name == "Arrive") as ApiCommand;
    const request = {
      DateTime: dateTime,
      ReasonLate: reasonLate,
    } as ArriveRequest;
    fetchApi<JsonResponse>({ url: link.Link, payload: request, method: link.Method })
      .then((response) => {
        if (response.Success) {
          track?.refresh();
          props.CloseDialog();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to arrive');
      })
      .finally(() => setSaving(false));
  }

  useEffect(() => {
    fetchApi<ArriveData>({ url: props.Link.Link, payload: {}, method: props.Link.Method })
      .then((data) => {
        setData(data);
        setDateTime(parseJSON(data.DateTime));
        setLoading(false);
      });
  }, [props.Link]);

  if (loading || !data) {
    return <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image"></div>
      <div className="k-loading-color"></div>
    </div>
  }
  return (
    <Dialog
      title={`Arrive (${data.Sequence} of ${data.StopCount}) - Pro # ${data.OrderNumber}`}
      onClose={props.CloseDialog}
      className='dialog-w1140'
    >
      {saving && <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Loader type="converging-spinner" />
      </Box>}

      {data.Notes.map((note, index) => {
        return (<div key={index} className="alert alert-info" role="alert">
          <strong>Stop Note</strong> {note}
        </div>);
      })}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body1">
            {data.LocationName !== null && <>{data.LocationName.toUpperCase()}<br /></>}
            {data.AddressLine1 !== null && <>{data.AddressLine1.toUpperCase()}<br /></>}
            {data.City}, {data.State} {data.ZipCode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="body1" gutterBottom>
            Scheduled {data.TypeName}{' '}
            <Link href="#" onClick={() => setDateTime(parseJSON(data.ScheduledDateTime))}>
              {formatDateTime(data.ScheduledDateTime)}
            </Link>
          </Typography>
          {data.ArrivalDateTime && (
            <Typography variant="body1" gutterBottom>
              Arrival{' '}
              <Link href="#" onClick={() => setDateTime(parseJSON(data.ArrivalDateTime!))}>
                {formatDateTime(data.ArrivalDateTime)}
              </Link>
            </Typography>
          )}
          {data.DepartDateTime && (
            <Typography variant="body1" gutterBottom>
              Departure {formatDateTime(data.DepartDateTime)}
            </Typography>
          )}
          {data.Miles > 0 && (
            <Typography variant="body1">
              {formatNumber(data.Miles, 'n0')} Stop Miles
            </Typography>
          )}
        </Grid>
      </Grid>

      <DateTimePicker
        required
        label='Arrival Date/Time'
        format="MM/dd/yyyy HH:mm"
        formatPlaceholder="formatPattern"
        value={dateTime}
        onChange={(e) => setDateTime(e.value)}
        width="100%"
      />

      <DropDownList
        defaultValue={0}
        label='Reason Late'
        value={data.ReasonsLate.find(x => x.ID === reasonLate)}
        data={data.ReasonsLate}
        textField="Name"
        style={{ width: '100%' }}
        onChange={(e) => setReasonLate(e.target.value.ID)}
      />

      <DialogActionsBar layout="end">
        <Button onClick={props.CloseDialog} disabled={saving}>Cancel</Button>
        <Button themeColor="primary" onClick={arrive} disabled={saving}>Arrive</Button>
      </DialogActionsBar>
    </Dialog>
  );
}

export default Arrive;
